/**
 * Extract data URL assets — converts inline data URLs to file references
 * Mutates components/settings to replace data URLs with asset paths
 */

interface AssetFile {
  name: string;
  bytes: Uint8Array;
}

export function extractDataUrlAssets(components: any, settings: any): { files: AssetFile[] } {
  const files: AssetFile[] = [];
  const seen = new Map<string, string>();

  const t = (v: any) => (typeof v === 'string' ? v.trim() : '');

  const simpleHash = (s: string) => {
    let h = 0;
    for (let i = 0; i < s.length; i++)
      h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return Math.abs(h).toString(36);
  };

  const dataUrlToBytes = (dataUrl: string) => {
    const idx = dataUrl.indexOf(',');
    if (idx === -1) return null;
    const meta = dataUrl.slice(0, idx);
    const payload = dataUrl.slice(idx + 1);

    const mime =
      (meta.match(/^data:([^;]+)/) || [])[1] ||
      'application/octet-stream';
    const isBase64 = /;base64/i.test(meta);

    try {
      let bin: string;
      if (isBase64) {
        bin = atob(payload);
      } else {
        bin = decodeURIComponent(payload);
      }
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return { mime, bytes };
    } catch {
      return null;
    }
  };

  const extFromMime = (mime: string) => {
    const m = String(mime || '').toLowerCase();
    if (m.includes('png')) return 'png';
    if (m.includes('jpeg') || m.includes('jpg')) return 'jpg';
    if (m.includes('webp')) return 'webp';
    if (m.includes('svg')) return 'svg';
    return 'bin';
  };

  const normalize = (dataUrl: string) => {
    const raw = t(dataUrl);
    if (!raw.startsWith('data:image/')) return raw;
    if (seen.has(raw)) return seen.get(raw)!;

    const parsed = dataUrlToBytes(raw);
    if (!parsed) return raw;

    const ext = extFromMime(parsed.mime);
    const name = `asset-${simpleHash(raw)}.${ext}`;
    const path = `assets/${name}`;
    seen.set(raw, path);
    files.push({ name, bytes: parsed.bytes });
    return path;
  };

  // Known image locations
  try {
    if (components?.header?.data?.logoUrl) {
      components.header.data.logoUrl = normalize(
        components.header.data.logoUrl,
      );
    }
    if (components?.hero?.data?.imageUrl) {
      components.hero.data.imageUrl = normalize(
        components.hero.data.imageUrl,
      );
    }
    if (components?.team?.data?.members?.length) {
      components.team.data.members = components.team.data.members.map(
        (m: any) => ({
          ...m,
          image: m.image ? normalize(m.image) : m.image,
        }),
      );
    }
    if (components?.gallery?.data?.images?.length) {
      components.gallery.data.images = components.gallery.data.images.map(
        (img: any) => ({
          ...img,
          url: img.url ? normalize(img.url) : img.url,
        }),
      );
    }
  } catch {
    // fail-safe: if anything goes wrong, return without assets rewriting
    return { files: [] };
  }

  return { files };
}
